import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Spin, Alert } from 'antd';
import { DocViewer } from '../ApiDocs/DocViewer';
import { Navigation } from '../ApiDocs/Navigation';
import './PortalDocsPage.css';

const { Sider, Content } = Layout;

interface DocFile {
  path: string;
  title: string;
  category: string;
}

const PORTAL_DOC_STRUCTURE: DocFile[] = [
  { path: 'README', title: 'Overview', category: 'main' },
  { path: 'pages/events', title: 'Events Calendar', category: 'pages' },
  { path: 'pages/climate-recap', title: 'Climate Recap', category: 'pages' },
  { path: 'pages/forecast', title: 'Forecast', category: 'pages' },
  { path: 'pages/nowcasting', title: 'Nowcasting', category: 'pages' },
  { path: 'pages/observations', title: 'Observations', category: 'pages' },
  { path: 'pages/alerts', title: 'Alerts', category: 'pages' },
  { path: 'pages/timelines-api', title: 'Timelines API', category: 'pages' },
];

export const PortalDocsPage = () => {
  const { section, page } = useParams<{ section?: string; page?: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);
      setError(null);

      let filePath: string;
      if (!section && !page) {
        filePath = 'README';
      } else if (section && page) {
        filePath = `${section}/${page}`;
      } else if (page) {
        filePath = page;
      } else {
        filePath = 'README';
      }

      const url = `/portal-docs/${filePath}.md`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          if (response.status === 404) {
            setError(`Documentation file not found: ${filePath}.md`);
          } else {
            setError(`Failed to load documentation: ${response.statusText}`);
          }
          setContent('');
          return;
        }

        const text = await response.text();
        setContent(text);
      } catch (err) {
        setError(`Error loading documentation: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setContent('');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [section, page]);

  useEffect(() => {
    if (!loading && content && window.location.hash) {
      const hash = window.location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [loading, content]);

  const handleNavigation = (path: string) => {
    const parts = path.split('/');
    if (parts.length === 1) {
      if (path === 'README') {
        navigate('/portal-docs');
      } else {
        navigate(`/portal-docs/${path}`);
      }
    } else if (parts.length === 2) {
      navigate(`/portal-docs/${parts[0]}/${parts[1]}`);
    } else {
      navigate(`/portal-docs/${path}`);
    }
  };

  const getCurrentPath = (): string => {
    if (!section && !page) return 'README';
    if (section && page) return `${section}/${page}`;
    if (page) return page;
    return 'README';
  };

  const currentPath = getCurrentPath();

  return (
    <Layout className="portal-docs-layout api-docs-layout">
      <Sider
        width={280}
        className="api-docs-sider"
        theme="light"
        collapsible={false}
      >
        <Navigation
          docs={PORTAL_DOC_STRUCTURE}
          currentPath={currentPath}
          onNavigate={handleNavigation}
          title="Portal User Guide"
        />
      </Sider>
      <Content className="api-docs-content-wrapper portal-docs-content">
        {loading ? (
          <div className="api-docs-loading">
            <Spin size="large" />
          </div>
        ) : error ? (
          <Alert
            title="Error Loading Documentation"
            description={error}
            type="error"
            showIcon
            style={{ margin: '24px' }}
          />
        ) : (
          <DocViewer content={content} basePath="/portal-docs" />
        )}
      </Content>
    </Layout>
  );
};
